import React, { useState } from "react";
import { adminApi } from "../../apiAdmin";
import { withQuietRetry } from "../../utils/adminRetry";
import AdminLoadFailed from "../AdminLoadFailed";

// Disputes — this talent's chargebacks, fetched on first open. Moved out of
// pages/AdminDjProfile.tsx in the Phase C split.
export function DjDisputes({ id }: { id: string | undefined }) {
  const [showDisputes, setShowDisputes] = useState(false);
  const [djDisputes, setDjDisputes] = useState<Array<{
    _id: string; stripeDisputeId: string; amount: number; currency: string;
    reason?: string; status: string; evidenceDueBy?: string | null;
    customerEmail?: string; customerName?: string; createdAt: string;
    order?: { _id: string; searchQuery: string; amount: number; status: string } | null;
  }>>([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  // A list that didn't load is never "No disputes for this talent."
  const [disputesFailed, setDisputesFailed] = useState(false);

  const loadDisputes = async () => {
    if (!id) return;
    setDisputesLoading(true);
    setDisputesFailed(false);
    try {
      const res = await withQuietRetry(() => adminApi.get(`/dj/${id}/disputes`));
      setDjDisputes(res.data);
    } catch {
      setDjDisputes([]);
      setDisputesFailed(true);
    } finally {
      setDisputesLoading(false);
    }
  };

  const toggleDisputes = async () => {
    if (showDisputes) { setShowDisputes(false); return; }
    setShowDisputes(true);
    if (djDisputes.length === 0 && id) await loadDisputes();
  };

  return (
    <div className="adm-card adm-mb-16">
      <div
        className="card-header-flex"
        style={{ cursor: "pointer", marginBottom: showDisputes ? 12 : 0 }}
        onClick={toggleDisputes}
      >
        <h2 className="adm-section-title">
          <span style={{ display: "inline-block", transition: "transform 0.2s", transform: showDisputes ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem", marginRight: 8 }}>▶</span>
          Disputes {djDisputes.length > 0 ? `(${djDisputes.length})` : ""}
        </h2>
      </div>
      {showDisputes && (
        <div>
          {disputesLoading ? (
            <p className="muted-text adm-fs-80" style={{ padding: "8px 0" }}>Loading disputes...</p>
          ) : disputesFailed ? (
            <AdminLoadFailed inline what="this talent's disputes" onRetry={loadDisputes} />
          ) : djDisputes.length === 0 ? (
            <p className="muted-text adm-fs-80" style={{ padding: "8px 0" }}>No disputes for this talent.</p>
          ) : (
            <div className="adm-table-wrapper">
              <table className="adm-table-light adm-table-compact">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th>Client</th>
                    <th>Evidence Due</th>
                    <th>Order</th>
                  </tr>
                </thead>
                <tbody>
                  {djDisputes.map((d) => {
                    const isOverdue = d.evidenceDueBy && new Date(d.evidenceDueBy) <= new Date();
                    const statusLabel = d.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                    const statusClass = d.status.includes("won") ? "badge-accepted"
                      : d.status.includes("review") ? "badge-queued"
                      : "badge-declined";
                    return (
                      <tr key={d._id} className="hover-row">
                        <td className="adm-nowrap adm-fs-78">
                          {new Date(d.createdAt).toLocaleDateString()}
                        </td>
                        <td><span className={`badge badge-sm ${statusClass}`}>{statusLabel}</span></td>
                        <td className="adm-bold">${(d.amount / 100).toFixed(2)}</td>
                        <td className="adm-fs-78">
                          {d.reason ? d.reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—"}
                        </td>
                        <td className="adm-fs-78">
                          {d.customerName || d.customerEmail || "—"}
                        </td>
                        <td className="adm-fs-78">
                          {d.evidenceDueBy ? (
                            <span style={{ color: isOverdue ? "var(--adm-red)" : "var(--adm-orange)", fontWeight: isOverdue ? 700 : undefined }}>
                              {new Date(d.evidenceDueBy).toLocaleDateString()}
                              {isOverdue && " (overdue)"}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="muted-text adm-fs-76">
                          {d.order?.searchQuery?.slice(0, 20) || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
